// Import Libraries
import React, { useState, useEffect, useContext, useRef } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCreditCard, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

// Import Components
import UpgradeMembership from './upgradeMembership.js';

// Import Services
import userService from '../../../services/userService.js';

// Context
import { UserContext } from '../../../context/User';

// Setup Modal to Root
Modal.setAppElement('#root');

const BRAND_PURPLE = '#7A64EB';
const BRAND_NAVY = '#1A1161';

/**
 * One-time notice shown after Stripe cancels a subscription because the
 * renewal payment never went through. The backend sets
 * user.downgradeNoticePending; this marks it seen as soon as it's shown, so
 * it pops exactly once whether the user reactivates, closes it, or just
 * navigates away.
 */
const PaymentFailedDowngrade = () => {
  const { user, setUser } = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);
  const [upgradeIsOpen, setUpgradeIsOpen] = useState(false);
  // A user refetch can land before the "seen" call commits and bring the
  // flag back -- never show it twice in one session regardless.
  const shownRef = useRef(false);

  useEffect(() => {
    if (!user?.downgradeNoticePending || shownRef.current) return;
    if (!window.location.pathname.startsWith('/app/')) return;

    shownRef.current = true;
    setIsOpen(true);
    userService.markDowngradeNoticeSeen();
    // Clear it locally too, so the cached user can't re-trigger it.
    setUser({ ...user, downgradeNoticePending: false });
  }, [user, setUser]);

  const reactivate = () => {
    userService.addActionLog('Downgrade Notice - Reactivate Pro');
    setIsOpen(false);
    setUpgradeIsOpen(true);
  };

  const dismiss = () => {
    userService.addActionLog('Downgrade Notice - Dismissed');
    setIsOpen(false);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={dismiss}
        contentLabel="Moved to Free plan"
        className="modal"
        style={{ content: { maxWidth: 460, width: '90vw', borderRadius: 24, padding: '36px 40px' } }}
      >
        <div
          role="button"
          aria-label="Close"
          onClick={dismiss}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #ddd',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: BRAND_NAVY,
              marginBottom: 20,
            }}
          >
            <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: 44, color: BRAND_PURPLE }} />
            <FontAwesomeIcon
              icon={faCircleExclamation}
              style={{
                position: 'absolute',
                right: 22,
                bottom: 24,
                fontSize: 18,
                color: '#E5636B',
                backgroundColor: BRAND_NAVY,
                borderRadius: '50%',
              }}
            />
          </div>

          <div style={{ color: BRAND_PURPLE, fontSize: 24, fontWeight: 700, marginBottom: 16 }}>
            You are on a Free plan now
          </div>

          <p style={{ fontSize: 16, lineHeight: '24px', margin: '0 0 12px' }}>
            Looks like your last payment didn't go through, so we've moved you to a free account.
          </p>
          <p style={{ fontSize: 16, lineHeight: '24px', margin: '0 0 28px' }}>
            Your projects are all here — view them any time.
          </p>

          <button
            onClick={reactivate}
            style={{
              width: '100%',
              padding: '16px',
              border: 'none',
              borderRadius: 999,
              backgroundColor: BRAND_PURPLE,
              color: '#fff',
              fontSize: 17,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Reactivate Pro
          </button>
        </div>
      </Modal>

      <UpgradeMembership
        isOpen={upgradeIsOpen}
        onRequestClose={() => setUpgradeIsOpen(false)}
        includeFree={false}
      />
    </>
  );
};

export default PaymentFailedDowngrade;
